import Table from "../../../components/Table/Table"
import { useState, useEffect, useMemo } from "react"
import axios from "axios"
import { Box, Button, Fade, LinearProgress, Typography } from "@mui/material"
import { useAppDispatch } from "../../../store"
import { setCurrentPage } from "../../../store/auth/authReducer"
import Filter from "../../../components/Filter/Filter"
import { useSelector } from "react-redux"
import { getFilterValues } from "../../../utils/getFilterValues"
import { logOut } from "../../../utils/logOut"
import SimplifiedTable from "../../../components/Table/SimpleTable"
import { onChangeCallsFilter } from "../../../store/filter/filterReducer"
import { CallFilterData } from "../../../components/Filter/FilterData"
import TranslatedText from "../../../utils/Translate"
import { useSnackbar } from "../../../utils/SnackbarContext"

export default function Calls() {
    //Declaring states
    const [tableData, setTableData] = useState([])
    const [loading, setLoading] = useState(false)
    const [startRow, setStartRow] = useState(0)
    const [pageRows, setPageRows] = useState(100)
    const [maxRows, setMaxRows] = useState(pageRows)
    const [totalRows, setTotalRows] = useState(0)

    const { showSnackbar } = useSnackbar();

    //Set header title
    const dispatch = useAppDispatch()
    
    useEffect(() => {
        dispatch(setCurrentPage('Calls'))
    }, [dispatch]);

    const filters = useSelector((state: any) => state.filter.filterUserData.calls)


    // Making a request to get data for a table or download a file
    async function fetchData(params: any = {}) {
        setLoading(true)

        let downloadFile: boolean = Boolean((params['downloadcsv'] && params['downloadcsv'] != '0') || (params['downloadmp3'] && params['downloadmp3'] != '0'))

        if (!downloadFile) {
            params = { ...params, calculatetotalcount: true }
        }

        try {
            const response = await axios.get('https://cardgarantsales.com/restapi/call', {
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": "Bearer " + localStorage.getItem('access_token')
                },
                params: { ...params, startRow, maxRows },
                responseType: downloadFile ? 'blob' : 'json'
            })

            if (downloadFile) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'report.zip');
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else if (response.data !== null) {
                if (typeof (response.data) === 'object') {
                    setTotalRows(+response.data[0].totalcount);
                    setTableData(response.data.slice(1));
                } else {
                    showSnackbar('ERROR: INVALID SERVER RESPONSE', 'error');
                }
            }
            setLoading(false);

            if (downloadFile) {
                dispatch(onChangeCallsFilter({ type: 'downloadcsv', value: '0' }))
                dispatch(onChangeCallsFilter({ type: 'downloadmp3', value: '0' }))
                let updatedFilters = filters.map((el: any) => {
                    if (el.name == 'Download CSV') {
                        return {
                            ...el, params: [{
                                "type": "downloadcsv",
                                "label": "Download CSV",
                                "value": "0",
                                "input": "checkbox"
                            }]
                        }
                    }
                    else if (el.name == 'Download Mp3') {
                        return {
                            ...el, params: [{
                                "type": "downloadmp3",
                                "label": "Download Mp3",
                                "value": "0",
                                "input": "checkbox"
                            }]
                        }
                    }
                    else return el
                });

                localStorage.setItem(`calls_filter`, JSON.stringify(updatedFilters));
            }



        } catch (error: any) {
            console.log(error);
            if (error.code === "ERR_BAD_REQUEST") {
                logOut(dispatch);
            } else if (error.code === "ERR_NETWORK") {
                showSnackbar('ERROR NETWORK', 'error')
            }
            setLoading(false);
        }
    }


    //on load page send request to get table data with filters
    useEffect(() => {
        fetchData(getFilterValues(filters))
    }, [startRow, maxRows])

    useEffect(() => {
        setMaxRows(pageRows)
        setStartRow(0)
    }, [filters])

    //Pagination buttons
    function handleBack() {
        if ((startRow - pageRows) < 0) {
            setStartRow(0)
            setMaxRows(pageRows)

        }
        else {
            setStartRow((prev) => prev - pageRows)
            setMaxRows((prev) => prev - pageRows)
        }
    }

    function handleNext() {

        setStartRow((prev) => prev + pageRows)
        setMaxRows((prev) => prev + pageRows)

    }

    function handleEnd() {

        setStartRow(totalRows % pageRows === 0 ? totalRows - pageRows : totalRows - (totalRows % pageRows))
        setMaxRows(totalRows % pageRows === 0 ? totalRows : totalRows - (totalRows % pageRows) + pageRows)

    }

    function handleStart() {

        setStartRow(0)
        setMaxRows(pageRows)

    }

    const totalPages = Math.ceil(totalRows / pageRows);
    const currentPage = Math.ceil(maxRows / pageRows);

    const accesslevel = useSelector(
        (state: any) => state.auth.authData.accesslevel
    )

    const memoizedTable = useMemo(() => <SimplifiedTable rows={typeof (tableData) === 'object' ? tableData : []} page={'calls'} />, [tableData]);

    return (
        <Box sx={{ height: '86vh', display: 'flex', flexDirection: 'column' }}>
            <Filter exclude={accesslevel !== "5" ? ['partnerbank'] : []} fetchData={(params: object) => fetchData(params)} page="calls" filtersList={CallFilterData} />
            {loading ? <LinearProgress sx={{ width: "50%", marginTop: '33vh', marginLeft: 'auto', marginRight: 'auto' }} /> :
                <>
                    {memoizedTable}
                    <Fade in={!loading} timeout={300}>
                        <Box sx={{ display: 'flex', alignItems: "center", marginTop: 'auto' }}>
                            <Box sx={{ mt: 2 }}>
                                <Button disabled={startRow === 0} onClick={() => handleStart()} size="small" sx={{ mr: 1 }} variant="outlined" >{"<<"}</Button>
                                <Button disabled={startRow === 0} onClick={() => handleBack()} size="small" sx={{ mr: 1 }} variant="outlined" ><span>{'<'}&nbsp;</span> {<TranslatedText text={"Back"} />}</Button>
                                <Button disabled={maxRows >= totalRows} onClick={() => handleNext()} size="small" sx={{ mr: 1 }} variant="outlined" >{<TranslatedText text={'Next'} />}&nbsp;<span>{'>'}</span></Button>
                                <Button disabled={maxRows >= totalRows || tableData.length < pageRows} onClick={() => handleEnd()} size="small" sx={{ mr: 1 }} variant="outlined" >{">>"}</Button>
                            </Box>
                            <Typography sx={{ pt: 2, ml: 2 }}><TranslatedText text={'Rows on page:'} /> {pageRows}</Typography>
                            <Typography sx={{ pt: 2, ml: 2 }}>
                                <TranslatedText text={'Page:'} /> {currentPage} / {totalPages}
                            </Typography>
                            <Typography sx={{ pt: 2, ml: 2 }}><TranslatedText text={'Total rows:'} /> {totalRows}</Typography>
                        </Box>
                    </Fade>
                </>
            }
        </Box>
    )
}